import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CREDIT_RATES, MAX_DICTATION_SECONDS } from '@lyrmo/shared';
import type { ApiClient } from '../../api/client';
import { ApiError } from '../../api/api-error';
import { Icon } from '../../components/Icon';
import { RollingNumber } from '../../components/RollingNumber';
import { cx } from '../../lib/cx';
import { useI18n } from '../../i18n/i18n-context';
import { useTrack } from '../../lib/tracking-context';
import { createRecordedDictation, dictationLimit, recordingMediaType } from './recorded-dictation';
import { SpeechGraph } from './SpeechGraph';
import { TranscriptGraph } from './TranscriptGraph';
import type { SpeechPlace } from './transcript-graph';

const COPY = {
  en: {
    graph: 'Speech level',
    start: 'Dictate',
    stopShort: 'Stop',
    stop: 'Stop dictation',
    cancel: 'Cancel dictation',
    starting: 'Starting microphone…',
    recording: 'Listening · transcript appears after you stop',
    finishing: 'Finishing transcript…',
    hint: `Audio to text · ${CREDIT_RATES.transcriptionPerSecond} credits per second (${CREDIT_RATES.transcriptionPerSecond * 60} per minute), up to 3 minutes. Audio is sent to OpenAI for transcription after you stop.`,
    unsupported:
      'Voice dictation is not supported here. Try a browser with microphone access over HTTPS.',
    permission:
      'Microphone access was denied. Allow it in your browser or system settings and try again.',
    failed: 'Voice dictation failed. Please try recording again.',
    noCredits: 'Not enough credits left today to dictate.',
    /** Beside the timer: when the credits left stop the recording. */
    creditsEnd: (time: string) => `Today’s credits run out at ${time}`,
    creditsStopped: (time: string) => `Stopped at ${time}: today’s credits ran out.`,
  },
  uk: {
    graph: 'Рівень голосу',
    start: 'Диктувати',
    stopShort: 'Зупинити',
    stop: 'Зупинити диктування',
    cancel: 'Скасувати диктування',
    starting: 'Вмикаємо мікрофон…',
    recording: 'Слухаємо · текст з’явиться після зупинки',
    finishing: 'Завершуємо розпізнавання…',
    hint: `Аудіо в текст · ${CREDIT_RATES.transcriptionPerSecond} кредитів за секунду (${CREDIT_RATES.transcriptionPerSecond * 60} за хвилину), до 3 хвилин. Аудіо надсилається до OpenAI для розпізнавання після зупинки.`,
    unsupported:
      'Голосове диктування тут не підтримується. Спробуйте браузер із доступом до мікрофона через HTTPS.',
    permission:
      'Доступ до мікрофона заборонено. Дозвольте його в налаштуваннях браузера або системи.',
    failed: 'Не вдалося розпізнати голос. Спробуйте записати ще раз.',
    noCredits: 'На сьогодні не вистачає кредитів для диктування.',
    creditsEnd: (time: string) => `Кредити на сьогодні закінчаться на ${time}`,
    creditsStopped: (time: string) => `Зупинено на ${time}: кредити на сьогодні закінчилися.`,
  },
};

export function DictationControl({
  transcribe,
  getConfig,
  creditsLeft,
  onActiveChange,
  onTranscript,
}: {
  readonly transcribe?: ApiClient['transcribeAudio'] | undefined;
  readonly getConfig?: ApiClient['getDictationConfig'] | undefined;
  /** Today's credits left, once known: the recording stops by itself before they run out. */
  readonly creditsLeft?: number | undefined;
  readonly onActiveChange: (active: boolean) => void;
  readonly onTranscript: (text: string) => void;
}) {
  const { locale, describeError } = useI18n();
  const track = useTrack();
  const copy = COPY[locale === 'uk' ? 'uk' : 'en'];
  type Status = 'idle' | 'starting' | 'recording' | 'finishing';
  const [state, setState] = useState({
    transcribe,
    getConfig,
    status: 'idle' as Status,
  });
  // Changing API origins cancels the old recording in the effect below.
  const status =
    state.transcribe === transcribe && state.getConfig === getConfig ? state.status : 'idle';
  function setStatus(status: Status) {
    setState({ transcribe, getConfig, status });
  }
  const enabled = Boolean(transcribe);
  const [error, setError] = useState<string | null>(null);
  // Said once the transcript is in when the credits left stopped the recording; it goes as
  // errors do.
  const [note, setNote] = useState<string | null>(null);
  // The recording's length limit, in seconds, while it lasts; credits-bound when below 3 minutes.
  const [limit, setLimit] = useState(MAX_DICTATION_SECONDS);
  const controlRef = useRef<HTMLDivElement>(null);
  // A failure's note stays until the next recording, or until another of the editor's tools is
  // used: it's about the recording, and the writer has moved on.
  useEffect(() => {
    if (!error && !note) return;
    const editor = controlRef.current?.closest('.editor') ?? document;
    const used = (event: Event) => {
      const control =
        event.target instanceof Element
          ? event.target.closest('button, [role^="menuitem"], [role="option"]')
          : null;
      if (control && !controlRef.current?.contains(control)) {
        setError(null);
        setNote(null);
      }
    };
    editor.addEventListener('click', used);
    return () => {
      editor.removeEventListener('click', used);
    };
  }, [error, note]);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  // The speech graph's levels, and where it was at Stop, made a voice note while the transcript
  // comes. The recording's length is the timer's, from when listening started.
  const levels = useRef<number[]>([]);
  const [listeningAt, setListeningAt] = useState(0);
  const [spoken, setSpoken] = useState<{
    levels: readonly number[];
    stoppedAt: number;
    from?: SpeechPlace | undefined;
  }>({ levels: [], stoppedAt: 0 });
  const sessionRef = useRef<Pick<
    ReturnType<typeof createRecordedDictation>,
    'stop' | 'cancel'
  > | null>(null);
  const callbacks = useRef({ onActiveChange, onTranscript });
  useLayoutEffect(() => {
    callbacks.current = { onActiveChange, onTranscript };
  }, [onActiveChange, onTranscript]);
  useEffect(
    () => () => {
      sessionRef.current?.cancel();
      sessionRef.current = null;
      callbacks.current.onActiveChange(false);
    },
    [transcribe, getConfig],
  );

  function cancel() {
    sessionRef.current?.cancel();
    sessionRef.current = null;
    callbacks.current.onActiveChange(false);
    setStatus('idle');
    setAudioStream(null);
  }

  async function start() {
    if (!transcribe || sessionRef.current) return;
    setError(null);
    setNote(null);
    const { mediaDevices } = navigator as Partial<Navigator>;
    if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') {
      setError(copy.unsupported);
      return;
    }
    // Stops a second before today's credits run out, so all that was said is transcribed.
    const seconds = dictationLimit(creditsLeft);
    if (seconds < 1) {
      setError(copy.noCredits);
      return;
    }
    setLimit(seconds);
    let stoppedByCredits = false;
    setStatus('starting');
    levels.current = [];
    callbacks.current.onActiveChange(true);
    const controller = new AbortController();
    // Reserve the session while its server configuration is loading so Cancel and
    // account/API changes can abort it before the microphone opens.
    sessionRef.current = {
      stop: () => undefined,
      cancel: () => {
        controller.abort();
      },
    };
    function finish(text: string) {
      sessionRef.current = null;
      if (text) callbacks.current.onTranscript(text);
      if (text && stoppedByCredits) setNote(copy.creditsStopped(clock(seconds)));
      callbacks.current.onActiveChange(false);
      setStatus('idle');
      setAudioStream(null);
    }
    const options = {
      onListening: (stream: MediaStream) => {
        setListeningAt(Date.now());
        setAudioStream(stream);
        setStatus('recording');
      },
      onFinishing: (ended: 'stopped' | 'limit') => {
        stoppedByCredits = ended === 'limit' && seconds < MAX_DICTATION_SECONDS;
        // The speech graph is still on the page.
        const graph = controlRef.current?.querySelector('.speech-graph')?.getBoundingClientRect();
        setSpoken({
          levels: levels.current,
          stoppedAt: Date.now(),
          from: graph && { left: graph.left, width: graph.width },
        });
        setAudioStream(null);
        setStatus('finishing');
      },
      onComplete: finish,
      onError: (failure: unknown) => {
        finish('');
        const refused =
          failure instanceof DOMException &&
          (failure.name === 'NotAllowedError' || failure.name === 'SecurityError');
        if (refused) track({ event: 'microphone_blocked' });
        setError(
          refused
            ? copy.permission
            : failure instanceof ApiError
              ? describeError(failure)
              : copy.failed,
        );
      },
    };
    try {
      const config = getConfig ? await getConfig({ signal: controller.signal }) : { enabled: true };
      if (controller.signal.aborted) return;
      if (!config.enabled)
        throw new ApiError('SERVICE_UNAVAILABLE', 'Voice dictation is not configured.');
      if (!recordingMediaType()) {
        finish('');
        setError(copy.unsupported);
        return;
      }
      const session = createRecordedDictation({ ...options, transcribe, maxSeconds: seconds });
      sessionRef.current = session;
      void session.start();
    } catch (failure) {
      if (!controller.signal.aborted) options.onError(failure);
    }
  }

  const recording = status === 'recording';
  // Idle, this is a labelled button leading the editor's toolbar, as voice is a main way to
  // write. While active it takes over the toolbar.
  return (
    <>
      <div ref={controlRef} className={cx('dictation-control', status !== 'idle' && 'is-active')}>
        <button
          type="button"
          className={cx('button dictation-record', recording && 'is-recording')}
          aria-label={recording ? copy.stop : copy.start}
          title={recording ? copy.stop : copy.hint}
          disabled={status === 'starting' || status === 'finishing' || !enabled}
          onClick={() => {
            if (recording) sessionRef.current?.stop();
            else void start();
          }}
        >
          <Icon name={recording ? 'stop' : 'microphone'} />
          <span className="dictation-record-text">{recording ? copy.stopShort : copy.start}</span>
        </button>
        {recording && audioStream && (
          <>
            <SpeechGraph stream={audioStream} label={copy.graph} history={levels} />
            <Elapsed
              since={listeningAt}
              limit={limit}
              limitLabel={limit < MAX_DICTATION_SECONDS ? copy.creditsEnd(clock(limit)) : undefined}
            />
          </>
        )}
        {status === 'finishing' && (
          <TranscriptGraph
            levels={spoken.levels}
            length={clock(Math.floor((spoken.stoppedAt - listeningAt) / 1_000))}
            from={spoken.from}
          />
        )}
        {/* Always mounted so screen readers announce each change of state; only starting is
            shown, as the graphs show the rest. */}
        <p role="status" className={cx('dictation-status', status !== 'starting' && 'sr-only')}>
          {status !== 'idle' && copy[status]}
          {recording && limit < MAX_DICTATION_SECONDS && ` · ${copy.creditsEnd(clock(limit))}`}
        </p>
        {status !== 'idle' && (
          <button
            type="button"
            className="button button--ghost icon-button editor-tool-icon"
            aria-label={copy.cancel}
            title={copy.cancel}
            onClick={cancel}
          >
            <Icon name="close" />
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="dictation-error">
          {error}
        </p>
      )}
      {note && !error && (
        <p role="status" className="dictation-error">
          {note}
        </p>
      )}
    </>
  );
}

/** A length of time as the timer shows it, as 1:05. */
function clock(seconds: number): string {
  const whole = Math.max(0, seconds);
  return `${String(Math.floor(whole / 60))}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * Listening time since `since`; it turns to the warning colour shortly before the automatic stop.
 * When the credits left set that stop, it's shown after the time, as 0:12 / 0:19.
 */
function Elapsed({
  since,
  limit,
  limitLabel,
}: {
  since: number;
  limit: number;
  limitLabel?: string | undefined;
}) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setSeconds(Math.floor((Date.now() - since) / 1_000));
    }, 250);
    return () => {
      clearInterval(timer);
    };
  }, [since]);
  return (
    <span
      className={cx('dictation-time', limit - seconds <= 15 && 'is-ending')}
      aria-hidden="true"
      title={limitLabel}
    >
      <RollingNumber value={clock(seconds)} />
      {limitLabel && <span className="dictation-time-limit"> / {clock(limit)}</span>}
    </span>
  );
}
